import { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Phone, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PRODUCTS } from '../data/products';
import { COMPANY, BRANDS, STATS, ARTICLES } from '../data/content';
import SEO from '../components/SEO';
import ProductImage from '../components/ProductImage';

// Products to feature in the hero carousel (flagship, digital, saddle, spindle, compact)
const HERO_PRODUCT_IDS = [1, 2, 5, 6, 9];

export default function HomePage() {
  const navigate = useNavigate();

  // ── Hero carousel state ──
  const heroProducts = useMemo(
    () => HERO_PRODUCT_IDS.map(id => PRODUCTS.find(p => p.id === id)),
    []
  );
  const [heroIdx, setHeroIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setHeroIdx(prev => (prev + 1) % heroProducts.length);
      setAnimKey(k => k + 1);
    }, 3800);
    return () => clearInterval(t);
  }, [heroProducts.length]);

  const setHero = (i) => {
    setHeroIdx(i);
    setAnimKey(k => k + 1);
  };

  const current = heroProducts[heroIdx];

  return (
    <>
      <SEO />
      <div data-testid="home-page">

      {/* ═══════════════════════════════
          HERO — Light, product showcase
          ═══════════════════════════════ */}
      <section className="pt-16 md:pt-18 min-h-[88vh] flex items-center bg-white relative overflow-hidden">
        {/* Precision dot grid texture */}
        <div className="precision-grid absolute inset-0 opacity-30 pointer-events-none" />

        {/* Faint measurement circle motif */}
        <div className="absolute right-0 top-0 bottom-0 w-[50%] pointer-events-none overflow-hidden">
          <svg viewBox="0 0 400 400" className="absolute -right-20 top-1/2 -translate-y-1/2 w-[480px] h-[480px] opacity-[0.04]">
            <circle cx="200" cy="200" r="180" stroke="#0F172A" strokeWidth="1" fill="none"/>
            <circle cx="200" cy="200" r="140" stroke="#0F172A" strokeWidth="0.5" fill="none"/>
            <circle cx="200" cy="200" r="90" stroke="#0F172A" strokeWidth="0.5" fill="none"/>
            <line x1="20" y1="200" x2="380" y2="200" stroke="#0F172A" strokeWidth="0.6"/>
            <line x1="200" y1="20" x2="200" y2="380" stroke="#0F172A" strokeWidth="0.6"/>
            {[0,45,90,135,180,225,270,315].map(a => (
              <line key={a}
                x1={200 + 175 * Math.cos(a * Math.PI / 180)}
                y1={200 + 175 * Math.sin(a * Math.PI / 180)}
                x2={200 + 185 * Math.cos(a * Math.PI / 180)}
                y2={200 + 185 * Math.sin(a * Math.PI / 180)}
                stroke="#0F172A" strokeWidth="1.5"
              />
            ))}
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full py-14 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* ── Left: Copy ── */}
          <div>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-[2px] bg-safety" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety">
                Est. {COMPANY.founded} · Coimbatore, India
              </span>
            </div>

            <h1 className="font-heading font-bold text-steel-900 uppercase leading-[0.9] tracking-tight mb-7">
              <span className="text-6xl md:text-7xl xl:text-8xl block">Precision</span>
              <span className="text-6xl md:text-7xl xl:text-8xl block text-safety">Instruments</span>
              <span className="text-3xl md:text-4xl xl:text-5xl block text-steel-400 font-normal mt-1">
                for Textile Spinning
              </span>
            </h1>

            <p className="text-steel-500 text-base md:text-lg italic leading-relaxed mb-10 max-w-md">
              When you can measure it, you can improve it.
            </p>

            {/* Stats — inline, compact */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10 pl-5 border-l-2 border-safety/40">
              {STATS.map(stat => (
                <div key={stat.label}>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-steel-900">
                    {stat.value.toLocaleString()}{stat.suffix}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-steel-400 mt-0.5">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/products')}
                className="bg-safety text-white font-heading uppercase text-sm tracking-wider px-7 py-3.5 hover:-translate-y-0.5 hover:bg-orange-600 transition-all duration-200 flex items-center justify-center gap-2"
              >
                Explore Instruments <ArrowRight size={15} />
              </button>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="border-2 border-steel-200 text-steel-600 font-heading uppercase text-sm tracking-wider px-7 py-3.5 hover:border-steel-900 hover:text-steel-900 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Phone size={14} /> Reach Out
              </a>
            </div>
          </div>

          {/* ── Right: Rotating product card ── */}
          <div className="relative">
            {/* Main image card */}
            <div className="border border-steel-200 bg-stone-50 relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <img
                key={`hero-${animKey}`}
                src={current.image}
                alt={current.name}
                className="w-full h-full object-contain p-8 product-reveal"
              />
              {/* Category badge */}
              <div className="absolute top-4 left-4">
                <span className="font-mono text-[10px] uppercase tracking-wider bg-white border border-steel-200 px-3 py-1.5 text-safety">
                  {current.cat}
                </span>
              </div>
            </div>

            {/* Product name + dots */}
            <div className="mt-4 flex items-start justify-between gap-4">
              <div className="label-up" key={`label-${animKey}`}>
                <p className="font-mono text-[10px] uppercase tracking-wider text-steel-400 mb-1">
                  #{String(current.id).padStart(2, '0')} of 9 instruments
                </p>
                <h3 className="font-heading text-lg md:text-xl font-bold uppercase tracking-wide text-steel-900 leading-tight">
                  {current.name}
                </h3>
                <p className="font-mono text-[10px] text-steel-400 mt-1 uppercase tracking-wider">
                  {current.sub}
                </p>
              </div>
              {/* Dot indicators */}
              <div className="flex items-center gap-1.5 mt-2 flex-shrink-0">
                {heroProducts.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setHero(i)}
                    className={`dot-indicator cursor-pointer border-none bg-none ${i === heroIdx ? 'active' : 'inactive'}`}
                  />
                ))}
              </div>
            </div>

            {/* "See all products" link below */}
            <button
              onClick={() => navigate('/products')}
              className="mt-4 font-mono text-[11px] uppercase tracking-wider text-steel-400 hover:text-safety transition-colors flex items-center gap-1"
            >
              See all 9 instruments <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════
          PROBLEM — Compact, 3 cards
          ═══════════════════════════════ */}
      <section className="py-16 md:py-20 border-t border-b border-steel-200" style={{ background: '#FAFAF8' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-[2px] bg-safety" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel-400">The Problem We Solve</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-steel-900 uppercase tracking-tight mb-10 max-w-2xl">
            Unnoticed load variations destroy yarn quality every day
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-steel-200">
            {[
              {
                num: '01',
                title: 'The Hidden Cost',
                desc: 'Excess or deficit loads in drafting rollers directly increase thick and thin places and neps. A 1 Kg load increase in the front roller creates 2.5 Kg excess load across the arm — drawing more power and accelerating machine wear.',
              },
              {
                num: '02',
                title: 'What We Measure',
                desc: 'Drafting roller loads, spindle concentricity, saddle positions, roller eccentricity, and dew point — the factors that directly control yarn quality.',
              },
              {
                num: '03',
                title: 'The Result',
                desc: 'Immediate improvement in count CV%, CSP, imperfections, and end breakage rates.',
                highlight: 'Payback measured in days, not months.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`p-8 bg-white ${i < 2 ? 'border-b md:border-b-0 md:border-r border-steel-200' : ''}`}
              >
                <span className="font-mono text-[11px] text-safety font-medium">{item.num}</span>
                <h3 className="font-heading text-lg uppercase tracking-wide text-steel-900 mt-2 mb-3">{item.title}</h3>
                <p className="text-steel-500 leading-relaxed text-sm">{item.desc}</p>
                {item.highlight && (
                  <p className="mt-3 font-heading text-sm uppercase tracking-wide text-safety">{item.highlight}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════
          FEATURED PRODUCTS
          ═══════════════════════════════ */}
      <section className="py-16 md:py-20 bg-white border-b border-steel-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-[2px] bg-safety" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel-400">Our Instruments</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-steel-900 uppercase tracking-tight">
                Featured Products
              </h2>
            </div>
            <button
              onClick={() => navigate('/products')}
              className="font-mono text-[11px] uppercase tracking-wider text-steel-400 hover:text-safety transition-colors flex items-center gap-1"
            >
              View All 9 <ChevronRight size={13} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-steel-200">
            {PRODUCTS.slice(0, 4).map((p, i) => (
              <div
                key={p.id}
                className={`product-card group cursor-pointer ${i < 3 ? 'border-r' : ''} border-b border-steel-200 hover:bg-orange-50/40 transition-colors duration-200`}
                onClick={() => navigate(`/products/${p.id}`)}
              >
                {/* Image — always full colour, no crop */}
                <div className="product-img-wrap" style={{ height: '180px' }}>
                  <ProductImage
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-contain p-4"
                    eager={i < 2}
                  />
                </div>
                <div className="p-5">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-safety mb-2">{p.cat}</p>
                  <h3 className="font-heading text-sm md:text-base uppercase tracking-wide text-steel-900 group-hover:text-safety transition-colors leading-tight mb-1">
                    {p.name}
                  </h3>
                  <p className="font-mono text-[10px] text-steel-400 uppercase tracking-wider mb-4">{p.sub}</p>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-safety opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    View Details <ArrowRight size={10} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════
          BRAND COMPATIBILITY — compact strip
          ═══════════════════════════════ */}
      <section className="py-10 border-b border-steel-200" style={{ background: '#FAFAF8' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-400 text-center mb-6">
            Compatible with all major manufacturers
          </p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {BRANDS.map(brand => (
              <span
                key={brand}
                className="font-heading text-base md:text-lg uppercase tracking-wider text-steel-300 hover:text-steel-600 transition-colors cursor-default"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════
          KNOWLEDGE PREVIEW — compact
          ═══════════════════════════════ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-[2px] bg-safety" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel-400">Knowledge Hub</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-steel-900 uppercase tracking-tight">
                Technical Insights
              </h2>
            </div>
            <button
              onClick={() => navigate('/knowledge')}
              className="font-mono text-[11px] uppercase tracking-wider text-steel-400 hover:text-safety transition-colors flex items-center gap-1"
            >
              View All <ChevronRight size={13} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-steel-200">
            {ARTICLES.slice(0, 3).map((a, i) => (
              <a
                key={i}
                href={COMPANY.blog}
                target="_blank"
                rel="noopener noreferrer"
                className={`group p-7 ${i < 2 ? 'border-b md:border-b-0 md:border-r border-steel-200' : ''} hover:bg-orange-50/40 transition-colors duration-200 block`}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-safety">{a.tag}</span>
                <h3 className="font-heading text-base uppercase tracking-wide text-steel-900 mt-2 mb-2 group-hover:text-safety transition-colors leading-tight">
                  {a.title}
                </h3>
                <p className="text-steel-400 text-sm leading-relaxed">{a.excerpt}</p>
                <span className="font-mono text-[10px] uppercase tracking-wider text-steel-300 mt-4 inline-flex items-center gap-1 group-hover:text-safety transition-colors">
                  Read More <ArrowRight size={10} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
